import { useState, useRef, useEffect } from "react";
import { voteDiscussion, deleteDiscussion, editDiscussion,
         postReply, editReply, deleteReply, getDiscussion,
         adminDeleteDiscussion, adminDeleteReply } from "../api";
import { useAuth } from "../context/AuthContext";

const typeConfig = {
  battle:  { icon: "⚔",  label: "guff battle",   bg: "#2a1510", color: "#FF5C35", border: "#3a2010" },
  hottake: { icon: "🔥", label: "hot take",       bg: "#1a1025", color: "#9F77DD", border: "#2a1a40" },
  scene:   { icon: "🎬", label: "scene reaction", bg: "#101a10", color: "#4a9a6a", border: "#1a3a1a" },
  predict: { icon: "🎯", label: "prediction",     bg: "#1a1500", color: "#9a8040", border: "#3a3010" },
  spoiler: { icon: "💀", label: "spoiler room",   bg: "#1a1010", color: "#9a4a4a", border: "#3a1a1a" },
  general: { icon: "💬", label: "general",       bg: "#101520", color: "#5a8aaa", border: "#1a3040" },
};

function timeAgo(isoString) {
  if (!isoString) return "";
  const s = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (s < 60)    return "just now";
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Avatar({ username, size = 26 }) {
  const letters = username
    ? username.split(/[\s_-]/).map(p => p[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const hue = username ? (username.charCodeAt(0) * 17 + (username.charCodeAt(1) || 0) * 7) % 360 : 200;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `hsl(${hue},35%,15%)`,
      color: `hsl(${hue},65%,60%)`,
      border: `0.5px solid hsl(${hue},35%,22%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.38, fontWeight: 700, flexShrink: 0,
      fontFamily: "'Bricolage Grotesque', sans-serif",
    }}>{letters}</div>
  );
}

// ── Three-dot menu ────────────────────────────────────────────────────────────
function DotMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          color: "#55555e", fontSize: 16, padding: "2px 6px", borderRadius: 6,
          lineHeight: 1, letterSpacing: 1,
        }}
        title="options"
      >•••</button>

      {open && (
        <div style={{
          position: "absolute", right: 0, top: 26, zIndex: 50,
          background: "#161618", border: "0.5px solid #2a2a2e",
          borderRadius: 10, padding: 4, minWidth: 130,
          boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
        }}>
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "8px 12px", background: "none", border: "none",
              color: "#c0c0c0", fontSize: 12, cursor: "pointer", borderRadius: 6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#1e1e22"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
          >
            <span>✏️</span> Edit
          </button>
          <button
            onClick={() => { setOpen(false); onDelete(); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "8px 12px", background: "none", border: "none",
              color: "#9a4a4a", fontSize: 12, cursor: "pointer", borderRadius: 6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#1e1212"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
          >
            <span>🗑️</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Admin trash button ────────────────────────────────────────────────────────
function AdminTrashBtn({ onDelete, label = "this" }) {
  const [confirm, setConfirm] = useState(false);
  const [busy,    setBusy]    = useState(false);

  async function doDelete() {
    setBusy(true);
    await onDelete();
    setBusy(false);
  }

  if (confirm) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          onClick={doDelete} disabled={busy}
          style={{
            padding: "2px 8px", borderRadius: 6, border: "none",
            background: "#9a2a2a", color: "#fff", fontSize: 10,
            cursor: busy ? "default" : "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >{busy ? "…" : "delete"}</button>
        <button
          onClick={() => setConfirm(false)}
          style={{
            padding: "2px 6px", borderRadius: 6, fontSize: 10,
            border: "0.5px solid #252528", background: "transparent",
            color: "#55555e", cursor: "pointer",
          }}
        >×</button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirm(true)}
      title={`Admin: delete ${label}`}
      style={{
        background: "transparent", border: "none", cursor: "pointer",
        color: "#7a2a2a", fontSize: 13, padding: "2px 4px", borderRadius: 4,
        lineHeight: 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.color = "#cc3333"; }}
      onMouseLeave={e => { e.currentTarget.style.color = "#7a2a2a"; }}
    >🗑</button>
  );
}

// ── Reply component ───────────────────────────────────────────────────────────
function ReplyItem({ reply, currentUser, token, discId, onUpdated, onDeleted, isAdmin }) {
  const isAuthor  = currentUser && currentUser.id === reply.user_id;
  const [editing, setEditing]  = useState(false);
  const [body,    setBody]     = useState(reply.body);
  const [busy,    setBusy]     = useState(false);
  const [confirm, setConfirm]  = useState(false);

  async function save() {
    if (!body.trim()) return;
    setBusy(true);
    const res = await editReply(discId, reply.id, body.trim(), token);
    setBusy(false);
    if (res.id) { onUpdated({ ...reply, body: res.body }); setEditing(false); }
  }

  async function doDelete() {
    setBusy(true);
    await deleteReply(discId, reply.id, token);
    onDeleted(reply.id);
  }

  return (
    <div style={{
      padding: "12px 0", borderBottom: "0.5px solid #1a1a1d",
      display: "flex", gap: 10,
    }}>
      <Avatar username={reply.user?.username} size={24} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#a0a0a8", fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {reply.user?.username || "anon"}
          </span>
          <span style={{ fontSize: 10, color: "#6a6a7a" }}>{timeAgo(reply.created_at)}</span>
          {isAuthor && !editing && !confirm && (
            <div style={{ marginLeft: "auto" }}>
              <DotMenu onEdit={() => setEditing(true)} onDelete={() => setConfirm(true)} />
            </div>
          )}
          {isAdmin && !isAuthor && !editing && !confirm && (
            <div style={{ marginLeft: isAuthor ? 0 : "auto" }}>
              <AdminTrashBtn
                label="reply"
                onDelete={async () => {
                  await adminDeleteReply(reply.id, token);
                  onDeleted(reply.id);
                }}
              />
            </div>
          )}
        </div>

        {editing ? (
          <>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={2}
              style={{
                width: "100%", background: "#111113", border: "0.5px solid #252528",
                borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#c0c0c0",
                fontFamily: "'Plus Jakarta Sans', sans-serif", resize: "vertical",
                outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button
                onClick={save} disabled={busy}
                style={{
                  padding: "5px 14px", borderRadius: 8, border: "none",
                  background: "#FF5C35", color: "#fff", fontSize: 11,
                  fontWeight: 700, cursor: busy ? "default" : "pointer",
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >{busy ? "saving…" : "Save"}</button>
              <button
                onClick={() => { setEditing(false); setBody(reply.body); }}
                style={{
                  padding: "5px 14px", borderRadius: 8,
                  border: "0.5px solid #252528", background: "transparent",
                  color: "#55555e", fontSize: 11, cursor: "pointer",
                }}
              >Cancel</button>
            </div>
          </>
        ) : confirm ? (
          <div style={{ fontSize: 12, color: "#9a4a4a", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Delete this reply?
            <button
              onClick={doDelete} disabled={busy}
              style={{
                marginLeft: 10, padding: "3px 10px", borderRadius: 6, border: "none",
                background: "#9a4a4a", color: "#fff", fontSize: 11, cursor: "pointer",
              }}
            >{busy ? "…" : "Yes, delete"}</button>
            <button
              onClick={() => setConfirm(false)}
              style={{
                marginLeft: 6, padding: "3px 10px", borderRadius: 6,
                border: "0.5px solid #252528", background: "transparent",
                color: "#55555e", fontSize: 11, cursor: "pointer",
              }}
            >Cancel</button>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#a0a0a8", lineHeight: 1.65, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {body}
          </div>
        )}
      </div>
    </div>
  );
}

// ── BattleCard / HotTakeCard / SceneCard / SpoilerCard ───────────────────────

function BattleCard({ d, onVote }) {
  const [voted, setVoted] = useState(d.user_vote || null);
  const [votes, setVotes] = useState({ a: d.votes?.a || 0, b: d.votes?.b || 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const total = votes.a + votes.b || 1;
  const pctA  = Math.round((votes.a / total) * 100);
  const pctB  = 100 - pctA;

  async function handleVote(side) {
    const prevVoted = voted;
    const prevVotes = { ...votes };
    const other = side === "a" ? "b" : "a";

    if (voted === side) {
      // Toggle off (un-vote)
      setVoted(null);
      setVotes(v => ({ ...v, [side]: Math.max(0, v[side] - 1) }));
    } else {
      // New vote or switch sides
      setVoted(side);
      setVotes(v => ({
        ...v,
        [side]: v[side] + 1,
        ...(prevVoted ? { [other]: Math.max(0, v[other] - 1) } : {}),
      }));
    }

    const res = await onVote(d.id, side);
    if (res?.votes) setVotes({ a: res.votes.a, b: res.votes.b });
    else { setVoted(prevVoted); setVotes(prevVotes); }
  }

  const sides = [
    { key: "a", label: d.side_a || "Side A", color: "#FF5C35", bg: "#2a1510", border: "#3a2010" },
    { key: "b", label: d.side_b || "Side B", color: "#7777dd", bg: "#10102a", border: "#1a1a3a" },
  ];

  return (
    <div style={{ background: "#161618", borderRadius: 10, overflow: "hidden", marginBottom: 12, border: "0.5px solid #252528" }}>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px 6px" }}>
        {sides.map(s => (
          <span key={s.key} style={{ fontSize: 12, fontWeight: 600, color: s.color, fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {s.label}
          </span>
        ))}
      </div>
      <div style={{ height: 5, background: "#1c1c1f", display: "flex" }}>
        <div style={{ width: `${pctA}%`, background: "#FF5C35", transition: "width 0.4s ease" }} />
        <div style={{ width: `${pctB}%`, background: "#7777dd", transition: "width 0.4s ease" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 14px 10px" }}>
        <span style={{ fontSize: 10, color: "#8a8a9e" }}>{pctA}% · {votes.a.toLocaleString()}</span>
        <span style={{ fontSize: 10, color: "#8a8a9e" }}>{pctB}% · {votes.b.toLocaleString()}</span>
      </div>
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 8, padding: "0 14px 12px" }}>
        {sides.map(s => {
          const isVoted = voted === s.key;
          return (
            <button key={s.key} onClick={() => handleVote(s.key)} style={{
              flex: 1, padding: "8px 0", borderRadius: 8,
              fontSize: 11, fontWeight: 600,
              background: isVoted ? s.color : s.bg,
              color: isVoted ? "#fff" : s.color,
              border: `0.5px solid ${isVoted ? s.color : s.border}`,
              transition: "all 0.15s", cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
              {isVoted ? "✓ voted" : `vote ${s.label}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HotTakeCard({ d, onVote }) {
  const [voted,  setVoted]  = useState(d.user_vote || null);
  const [counts, setCounts] = useState({ agree: d.votes?.agree || 0, disagree: d.votes?.disagree || 0 });
  const total    = counts.agree + counts.disagree || 1;
  const agreePct = Math.round((counts.agree / total) * 100);

  async function handleVote(side) {
    const prevVoted  = voted;
    const prevCounts = { ...counts };
    const other = side === "agree" ? "disagree" : "agree";

    if (voted === side) {
      setVoted(null);
      setCounts(c => ({ ...c, [side]: Math.max(0, c[side] - 1) }));
    } else {
      setVoted(side);
      setCounts(c => ({
        ...c,
        [side]: c[side] + 1,
        ...(prevVoted ? { [other]: Math.max(0, c[other] - 1) } : {}),
      }));
    }

    const res = await onVote(d.id, side);
    if (res?.votes) {
      setCounts({ agree: res.votes.agree, disagree: res.votes.disagree });
    } else {
      setVoted(prevVoted);
      setCounts(prevCounts);
    }
  }

  const sides = [
    { key: "agree",    label: "agree — valid take", color: "#4a9a6a", bg: "#0e2010", border: "#1a3a20" },
    { key: "disagree", label: "disagree — bakwaas",  color: "#9a4a4a", bg: "#1a1010", border: "#3a1a1a" },
  ];

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ height: 4, background: "#1c1c1f", borderRadius: 2, overflow: "hidden", display: "flex", marginBottom: 6 }}>
        <div style={{ width: `${agreePct}%`, background: "#4a9a6a", transition: "width 0.4s" }} />
        <div style={{ width: `${100 - agreePct}%`, background: "#9a4a4a", transition: "width 0.4s" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8a8a9e", marginBottom: 10 }}>
        <span>{agreePct}% agree · {counts.agree.toLocaleString()}</span>
        <span>{100 - agreePct}% disagree · {counts.disagree.toLocaleString()}</span>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {sides.map(s => {
          const isVoted = voted === s.key;
          return (
            <button key={s.key} onClick={() => handleVote(s.key)} style={{
              flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 11, fontWeight: 600,
              background: isVoted ? s.color : s.bg,
              color: isVoted ? "#fff" : s.color,
              border: `0.5px solid ${isVoted ? s.color : s.border}`,
              transition: "all 0.15s", cursor: "pointer",
            }}>{isVoted ? `✓ ${s.key}d` : s.label}</button>
          );
        })}
      </div>
    </div>
  );
}

function SceneCard({ d }) {
  return (
    <div style={{
      background: "#0e1a0e", borderLeft: "2px solid #4a9a6a",
      borderRadius: "0 8px 8px 0", padding: "10px 14px", marginBottom: 12,
    }}>
      {d.scene_tag && <div style={{ fontSize: 10, color: "#4a9a6a", marginBottom: 4 }}>{d.scene_tag}</div>}
      {d.quote     && <div style={{ fontSize: 13, color: "#8a8a9a", fontStyle: "italic", lineHeight: 1.65 }}>"{d.quote}"</div>}
    </div>
  );
}

function SpoilerCard({ d }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: "#140a0a", border: "0.5px solid #3a1a1a", borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
      {!open ? (
        <>
          <div style={{ fontSize: 11, color: "#9a4a4a", marginBottom: 4 }}>⚠ spoiler warning — full plot details inside</div>
          <div style={{ fontSize: 12, color: "#55555e", lineHeight: 1.6, marginBottom: 10 }}>
            Only enter if you have watched the full film.
          </div>
          <button onClick={() => setOpen(true)} style={{
            background: "#2a1010", color: "#9a4a4a", border: "0.5px solid #3a1a1a",
            borderRadius: 20, padding: "6px 16px", fontSize: 11, cursor: "pointer",
          }}>enter spoiler room →</button>
        </>
      ) : (
        <div style={{ fontSize: 12, color: "#c0c0c0", lineHeight: 1.7 }}>{d.question}</div>
      )}
    </div>
  );
}

// ── Main DiscussionCard ────────────────────────────────────────────────────────
export default function DiscussionCard({ d, showToast, onDelete, onUpdate }) {
  const { currentUser, token, showAuthGate } = useAuth();
  const tc       = typeConfig[d.type] || typeConfig.hottake;
  const isAuthor = currentUser && currentUser.id === d.user_id;
  const isAdmin  = currentUser?.is_admin === true;

  const totalVotes = (d.votes?.a || 0) + (d.votes?.b || 0) +
                     (d.votes?.agree || 0) + (d.votes?.disagree || 0);

  // edit/delete state for the discussion
  const [editing,      setEditing]      = useState(false);
  const [editQuestion, setEditQuestion] = useState(d.question);
  const [editSideA,    setEditSideA]    = useState(d.side_a || "");
  const [editSideB,    setEditSideB]    = useState(d.side_b || "");
  const [editBusy,     setEditBusy]     = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy,   setDeleteBusy]   = useState(false);

  // local discussion data (can be updated after edit)
  const [disc, setDisc] = useState(d);

  // replies state
  const [repliesOpen,   setRepliesOpen]   = useState(false);
  const [replies,       setReplies]       = useState([]);
  const [replyCount,    setReplyCount]    = useState(d.reply_count || 0);
  const [loadingReplies,setLoadingReplies]= useState(false);
  const [replyBody,     setReplyBody]     = useState("");
  const [postingReply,  setPostingReply]  = useState(false);
  const [copied,        setCopied]        = useState(false);

  async function loadReplies() {
    setLoadingReplies(true);
    try {
      const data = await getDiscussion(disc.id);
      setReplies(data.replies || []);
      setReplyCount((data.replies || []).length);
    } catch {
      //
    } finally {
      setLoadingReplies(false);
    }
  }

  function toggleReplies() {
    if (!repliesOpen) {
      setRepliesOpen(true);
      loadReplies();
    } else {
      setRepliesOpen(false);
    }
  }

  async function handleVote(disc_id, side) {
    if (!token) {
      showAuthGate?.("vote");
      return {};
    }
    try {
      return await voteDiscussion(disc_id, side, token);
    } catch {
      return {};
    }
  }

  async function saveEdit() {
    if (!editQuestion.trim()) return;
    setEditBusy(true);
    const res = await editDiscussion(disc.id, {
      question: editQuestion.trim(),
      side_a:   editSideA,
      side_b:   editSideB,
    }, token);
    setEditBusy(false);
    if (res.id) {
      setDisc(res);
      setEditing(false);
      onUpdate?.(res);
    } else {
      showToast?.(res.error || "Failed to save");
    }
  }

  async function doDelete() {
    setDeleteBusy(true);
    try {
      await deleteDiscussion(disc.id, token);
      onDelete?.(disc.id);
    } catch {
      showToast?.("Failed to delete — try again");
      setDeleteBusy(false);
    }
  }

  async function submitReply() {
    if (!token) { showAuthGate?.("reply"); return; }
    if (!replyBody.trim()) return;
    setPostingReply(true);
    try {
      const res = await postReply(disc.id, replyBody.trim(), token);
      if (res.id) {
        setReplies(prev => [...prev, res]);
        setReplyCount(c => c + 1);
        setReplyBody("");
      } else {
        // Token expired or other auth error
        const errMsg = res.error || res.msg || "Failed to post — please sign in again";
        showToast?.(errMsg);
      }
    } catch {
      showToast?.("Failed to post reply — check your connection");
    } finally {
      setPostingReply(false);
    }
  }

  const iStyle = {
    width: "100%", background: "#111113", border: "0.5px solid #252528",
    borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#c0c0c0",
    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div style={{ borderBottom: "0.5px solid #1c1c1f", padding: "18px 20px" }}>

      {/* Type badge + author menu */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          fontSize: 10, padding: "3px 10px", borderRadius: 10,
          fontWeight: 600, background: tc.bg, color: tc.color,
          border: `0.5px solid ${tc.border}`,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          {tc.icon} {tc.label}
        </div>
        {isAuthor && !editing && !deleteConfirm && (
          <DotMenu
            onEdit={() => {
              setEditQuestion(disc.question);
              setEditSideA(disc.side_a || "");
              setEditSideB(disc.side_b || "");
              setEditing(true);
            }}
            onDelete={() => setDeleteConfirm(true)}
          />
        )}
        {isAdmin && !isAuthor && !editing && !deleteConfirm && (
          <AdminTrashBtn
            label="discussion"
            onDelete={async () => {
              await adminDeleteDiscussion(disc.id, token);
              onDelete?.(disc.id);
            }}
          />
        )}
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div style={{
          background: "#1a1010", border: "0.5px solid #3a1a1a", borderRadius: 10,
          padding: "12px 14px", marginBottom: 12,
          fontSize: 12, color: "#c0c0c0", fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          Delete this guff? This can't be undone.
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              onClick={doDelete} disabled={deleteBusy}
              style={{
                padding: "6px 16px", borderRadius: 8, border: "none",
                background: "#9a4a4a", color: "#fff", fontSize: 11,
                fontWeight: 700, cursor: deleteBusy ? "default" : "pointer",
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >{deleteBusy ? "deleting…" : "Yes, delete"}</button>
            <button
              onClick={() => setDeleteConfirm(false)}
              style={{
                padding: "6px 16px", borderRadius: 8,
                border: "0.5px solid #252528", background: "transparent",
                color: "#55555e", fontSize: 11, cursor: "pointer",
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Question (or edit textarea) */}
      {editing ? (
        <div style={{ marginBottom: 10 }}>
          <textarea
            value={editQuestion}
            onChange={e => setEditQuestion(e.target.value)}
            rows={3}
            style={{ ...iStyle, resize: "vertical", marginBottom: 8 }}
          />
          {disc.type === "battle" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <input value={editSideA} onChange={e => setEditSideA(e.target.value)} placeholder="Side A" style={iStyle} />
              <input value={editSideB} onChange={e => setEditSideB(e.target.value)} placeholder="Side B" style={iStyle} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={saveEdit} disabled={editBusy}
              style={{
                padding: "6px 16px", borderRadius: 8, border: "none",
                background: "#FF5C35", color: "#fff", fontSize: 11,
                fontWeight: 700, cursor: editBusy ? "default" : "pointer",
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >{editBusy ? "saving…" : "Save"}</button>
            <button
              onClick={() => setEditing(false)}
              style={{
                padding: "6px 16px", borderRadius: 8,
                border: "0.5px solid #252528", background: "transparent",
                color: "#55555e", fontSize: 11, cursor: "pointer",
              }}
            >Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: 14, fontWeight: 700, color: "#e0e0e0",
          lineHeight: 1.4, marginBottom: 10,
        }}>{disc.question}</div>
      )}

      {/* Author row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Avatar username={disc.user?.username} />
        <span style={{ fontSize: 11, color: "#55555e" }}>
          {disc.user?.username || "anonymous"}
          {disc.user?.location ? ` · ${disc.user.location}` : ""}
        </span>
        <span style={{ fontSize: 10, color: "#6a6a7a", marginLeft: "auto" }}>
          {timeAgo(disc.created_at)}
        </span>
      </div>

      {/* Type-specific content */}
      {disc.type === "battle"  && <BattleCard  d={disc} onVote={handleVote} />}
      {disc.type === "hottake" && <HotTakeCard d={disc} onVote={handleVote} />}
      {disc.type === "scene"   && <SceneCard   d={disc} />}
      {disc.type === "spoiler" && <SpoilerCard d={disc} />}
      {disc.type === "predict" && (
        <div style={{ fontSize: 12, color: "#55555e", marginBottom: 12, fontStyle: "italic",
          fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          share your prediction in the replies ↓
        </div>
      )}

      {/* Footer */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button
          onClick={toggleReplies}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 11, color: repliesOpen ? "#FF5C35" : "#55555e",
            fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0,
          }}
        >
          💬 {replyCount} {replyCount === 1 ? "reply" : "replies"}
        </button>
        {totalVotes > 0 && (
          <span style={{ fontSize: 11, color: "#55555e" }}>
            🔥 {totalVotes.toLocaleString()} voted
          </span>
        )}
        <span
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/discussions/${disc.id}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{ fontSize: 11, color: copied ? "#4a9a6a" : "#55555e", marginLeft: "auto", cursor: "pointer" }}
        >{copied ? "✓ copied!" : "share"}</span>
      </div>

      {/* Replies panel */}
      {repliesOpen && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "0.5px solid #1c1c1f" }}>
          {loadingReplies ? (
            <div style={{ fontSize: 11, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>loading replies…</div>
          ) : (
            <>
              {replies.length === 0 && (
                <div style={{ fontSize: 11, color: "#55555e", marginBottom: 12, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  no replies yet — be the first
                </div>
              )}
              {replies.map(r => (
                <ReplyItem
                  key={r.id}
                  reply={r}
                  currentUser={currentUser}
                  token={token}
                  discId={disc.id}
                  isAdmin={isAdmin}
                  onUpdated={updated => setReplies(prev => prev.map(x => x.id === updated.id ? updated : x))}
                  onDeleted={rid => {
                    setReplies(prev => prev.filter(x => x.id !== rid));
                    setReplyCount(c => c - 1);
                  }}
                />
              ))}

              {/* Reply input */}
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <textarea
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  rows={2}
                  placeholder={token ? "write a reply…" : "sign in to reply"}
                  disabled={!token}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitReply(); }
                  }}
                  style={{
                    flex: 1, background: "#111113", border: "0.5px solid #252528",
                    borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#c0c0c0",
                    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
                    resize: "none",
                  }}
                />
                <button
                  onClick={token ? submitReply : () => showAuthGate?.("reply")}
                  disabled={postingReply}
                  style={{
                    padding: "0 16px", borderRadius: 8, border: "none",
                    background: "#FF5C35", color: "#fff", fontSize: 11,
                    fontWeight: 700, cursor: "pointer", alignSelf: "flex-end",
                    height: 36, fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  {postingReply ? "…" : "↑"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
